import dayjs from 'dayjs';
import { ChatMessagePulled } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import {
  ChatMessageCategory,
  ChatMessagePayloadType,
  CustomChatMessagePayloadType,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';

export interface Divider {
  id: string;
  payload_type: CustomChatMessagePayloadType;
  payload: string;
  pulled: ChatMessagePulled.YES;
}

function isUnreadMessage(chat: Chat, message: ChatMessage | null): boolean {
  if (!message) return false;
  return !chat.last_read_new_message_id || message.id > chat.last_read_new_message_id;
}

function createDivider(type: CustomChatMessagePayloadType, payload: string): Divider {
  return {
    id: crypto.randomUUID(),
    payload_type: type,
    payload,
    pulled: ChatMessagePulled.YES,
  };
}

function formatTime(time: number | string, format: string): string {
  return dayjs(time).format(format);
}

function getTimeInfo(
  currentTime: number | string,
  prevTime: number | string | undefined,
  prevIsGroupCreateMessage: boolean
) {
  const today = dayjs();
  const yesterday = today.subtract(1, 'day');
  const currentDay = dayjs(currentTime);
  const prevDay = prevTime ? dayjs(prevTime) : null;

  const isToday = currentDay.isSame(today, 'day');
  const isYesterday = currentDay.isSame(yesterday, 'day');
  const isOlderDay = currentDay.isBefore(yesterday, 'day');

  const prevIsToday = prevDay?.isSame(today, 'day') || false;
  const prevIsYesterday = prevDay?.isSame(yesterday, 'day') || false;
  const prevIsOlderDay =
    (prevDay?.isBefore(yesterday, 'day') && prevDay?.isSame(currentDay, 'day')) ||
    false;

  let dateLabel = '';
  let timeLabel = '';
  let isNewTimeSection = false;

  if (isToday && (!prevIsToday || prevIsGroupCreateMessage)) {
    dateLabel = 'Today';
    timeLabel = formatTime(currentTime, 'HH:mm');
    isNewTimeSection = true;
  } else if (isYesterday && (!prevIsYesterday || prevIsGroupCreateMessage)) {
    dateLabel = 'Yesterday';
    timeLabel = formatTime(currentTime, '[Yesterday,] HH:mm');
    isNewTimeSection = true;
  } else if (isOlderDay && (!prevIsOlderDay || prevIsGroupCreateMessage)) {
    dateLabel = formatTime(currentTime, 'MMM D, YYYY');
    timeLabel = formatTime(currentTime, 'MMM D, YYYY HH:mm');
    isNewTimeSection = true;
  }

  return { dateLabel, timeLabel, isNewTimeSection };
}

export function handleDividerMessage(
  chat: Chat,
  prevMessage: ChatMessage,
  message: ChatMessage,
  index: number,
  isSendMessageRef: boolean,
  hasMoreHistory: boolean,
  hiddenNewMessages: boolean
): Divider[] | undefined {
  const isFirst = index === 0;
  const dividers: Divider[] = [];
  const prevMessage_ = isFirst ? null : prevMessage;
  const prevMessageTime = prevMessage_?.create_at;
  const messageTime = message.create_at!;

  const isNewMessage = isUnreadMessage(chat, message);
  const prevIsNewMessage = isUnreadMessage(chat, prevMessage_);
  const prevIsGroupCreateMessage =
    prevMessage_?.payload_type === ChatMessagePayloadType.GROUP_CREATE;

  if (!hasMoreHistory && isFirst) {
    dividers.push(
      createDivider(
        CustomChatMessagePayloadType.DIVIDER_CREATE_CHAT,
        'DIVIDER_CREATE_CHAT'
      )
    );
    if (
      message?.payload_type === ChatMessagePayloadType.GROUP_CREATE ||
      message?.id === 0
    ) {
      return dividers;
    }
  }

  if (isNewMessage && isSendMessageRef) {
    if (hiddenNewMessages) {
      return undefined;
    }

    if (
      (!prevIsNewMessage || prevIsGroupCreateMessage) &&
      message.category === ChatMessageCategory.RECEIVE
    ) {
      dividers.push(
        createDivider(CustomChatMessagePayloadType.DIVIDER_DATE, 'New Message'),
        createDivider(
          CustomChatMessagePayloadType.DIVIDER_TIME,
          formatTime(messageTime, 'HH:mm')
        )
      );
    }

    return dividers;
  }

  const timeInfo = getTimeInfo(messageTime, prevMessageTime, prevIsGroupCreateMessage);

  if (timeInfo.isNewTimeSection) {
    dividers.push(
      createDivider(CustomChatMessagePayloadType.DIVIDER_DATE, timeInfo.dateLabel),
      createDivider(CustomChatMessagePayloadType.DIVIDER_TIME, timeInfo.timeLabel)
    );
  }

  return dividers;
}
